import { DateCondition, Filter } from '@fabric-ui/components';
import { DwQueryRequest, SortDir } from './QueryObject.types';

type QueryObjectMetric = DwQueryRequest['select']['metrics'][number];
type QueryObjectDimension = DwQueryRequest['select']['dims'][number];
type QueryObjectSorting = DwQueryRequest['sorting'][number];
type QueryObjectTop = DwQueryRequest['top'];

type Builder = {
  setGroupBy: (...groupBy: string[]) => Builder;
  setDimensions: (...dims: DwQueryRequest['select']['dims']) => Builder;
  setMetrics: (...metrics: DwQueryRequest['select']['metrics']) => Builder;
  setSorting: (...sorting: DwQueryRequest['sorting']) => Builder;
  setLimit: (size: number) => Builder;
  setOffset: (offset: number) => Builder;
  setTop: (top: QueryObjectTop) => Builder;
  setGroupOthers: (groupOthers: boolean) => Builder;
  setFilter: (filter: Filter) => Builder;
  setTimeRange: (timeRange: DateCondition) => Builder;
  setTimezone: (timezone: string, timezoneOffset?: number) => Builder;
  setDistinct: (v: boolean) => Builder;
  setMetricsFilterThreshold: (v: number) => Builder;
  setMetricFilterRootProj: (v: boolean) => Builder;
  clone: () => Builder;
  build: () => DwQueryRequest;
};

const construct = <T extends Partial<DwQueryRequest>>(initial: T) => {
  const state = { q: { ...initial } as Partial<DwQueryRequest> };

  const update = (patch: Partial<DwQueryRequest>) => {
    state.q = { ...state.q, ...patch };
    return builder;
  };

  const builder: Builder = {
    setDimensions: (...dims) => update({ select: { ...state.q.select, metrics: [...(state.q.select?.metrics || [])], dims } }),

    setMetrics: (...metrics) => update({ select: { ...state.q.select, dims: [...(state.q.select?.dims || [])], metrics } }),

    setGroupBy: (...groupBy) => update({ groupBy }),

    setSorting: (...sorting) => update({ sorting }),

    setLimit: size => update({ top: { ...state.q.top, groupOthers: state.q.top?.groupOthers ?? false, size } }),

    setOffset: offset => update({ top: { ...state.q.top, groupOthers: state.q.top?.groupOthers ?? false, offset } }),

    setTop: top => update({ top }),

    setGroupOthers: groupOthers => update({ top: { ...state.q.top, groupOthers } }),

    setFilter: filter => update({ filter }),

    setTimeRange: timeRange => update({ timeRange }),

    setTimezone: (timezone, timezoneOffset) => update({ timezone, timezoneOffset }),

    setDistinct: v => update({ distinct: v }),

    setMetricsFilterThreshold: v => update({ metricsFilterThreshold: v }),

    setMetricFilterRootProj: v => update({ metricFilterRootProj: v }),

    clone: () => construct(structuredClone(state.q)),

    build: () => structuredClone(state.q) as DwQueryRequest,
  };

  return builder;
};

const create = (projectionId: DwQueryRequest['projectionId']) => construct({ projectionId });

const from = (q: Partial<DwQueryRequest> & { projectionId: DwQueryRequest['projectionId'] }) => construct(q);

const metric = (name: string, alias?: string, options?: Pick<QueryObjectMetric, 'conf' | 'expression' | 'type'>): QueryObjectMetric => ({
  name,
  alias,
  ...options,
});

const dim = (name: string, alias?: string, options?: Pick<QueryObjectDimension, 'expression' | 'type'>): QueryObjectDimension => ({
  name,
  alias: alias || name,
  ...options,
});

const asc = (name: string, options?: Pick<QueryObjectSorting, 'path'>): QueryObjectSorting => ({
  name,
  dir: SortDir.ASC,
  ...options,
});

const desc = (name: string, options?: Pick<QueryObjectSorting, 'path'>): QueryObjectSorting => ({
  name,
  dir: SortDir.DESC,
  ...options,
});

const fromDefault = (projectionId: DwQueryRequest['projectionId']) =>
  from({
    projectionId,
    select: {
      dims: [],
      metrics: [],
    },
    sorting: [],
    top: {
      size: 25,
      offset: 0,
      groupOthers: false,
    },
    distinct: false,
    groupBy: [],
  });

export const queryObjectBuilder = {
  create,
  from,
  fromDefault,
  select: {
    metric,
    dim,
  },
  sorting: {
    asc,
    desc,
  },
};
